import { QueryFunctionContext, useMutation, useQuery } from 'react-query';

import { APIError } from '@custom-types/APIError';

import { CheckEntity } from './Enums';
import {
  Check,
  create as createCheckActionFn,
  getCheckIns,
  getTotalPeopleCount,
  Payload as CheckInPayload,
} from './service';

interface AnalyticsArguments {
  eventId: string;
  entityType?: CheckEntity;
  sessionId?: string;
}

type CheckInsCountKey = [
  'checkInsAnalytics',
  string /* eventId */,
  CheckEntity?,
  string? /* sessionId */
];

type TotalPeopleCountKey = ['peopleAnalytics', string /* eventId */];

const checksQuery = {
  getQueryKey: (
    eventId: string,
    entityType?: CheckEntity,
    sessionId?: string
  ): CheckInsCountKey => ['checkInsAnalytics', eventId, entityType, sessionId],
  queryFn: ({
    queryKey: [, eventId, entityType, sessionId],
  }: QueryFunctionContext<CheckInsCountKey>) => getCheckIns(eventId, entityType, sessionId),
};

const peopleCountQuery = {
  getQueryKey: (eventId: string): TotalPeopleCountKey => ['peopleAnalytics', eventId],
  queryFn: ({ queryKey: [, eventId] }: QueryFunctionContext<TotalPeopleCountKey>) =>
    getTotalPeopleCount(eventId),
};

const useStatistics = ({ eventId, sessionId, entityType }: AnalyticsArguments) => {
  const checksQueryRes = useQuery({
    queryKey: checksQuery.getQueryKey(eventId, entityType, sessionId),
    queryFn: checksQuery.queryFn,
    cacheTime: 0,
  });
  const peopleCountQueryRes = useQuery({
    queryKey: peopleCountQuery.getQueryKey(eventId),
    queryFn: peopleCountQuery.queryFn,
    cacheTime: 0,
  });

  const {
    data: peopleCount,
    isFetching: isPeopleLoading,
    refetch: refetchPeopleCount,
  } = peopleCountQueryRes;
  const {
    data: totalChecksData,
    isFetching: isCheckInsLoading,
    refetch: refetchCheckInCheckOut,
  } = checksQueryRes;

  const refetch = () => {
    refetchPeopleCount();
    refetchCheckInCheckOut();
  };

  return {
    loading: isPeopleLoading || isCheckInsLoading,
    peopleCount,
    checkInCount: totalChecksData?.[0]?.value?.uniquePeopleCheckins,
    checkOutCount: totalChecksData?.[0]?.value?.uniquePeopleCheckouts,
    refetch,
  };
};

export default useStatistics;

export enum CheckActionErrorCode {
  Invalid = 'invalid_value',
  NotFound = 'person_not_found',
  NotAuthorized = 'user_not_authenticated',
  CheckOutNotEnabled = 'checkout_not_enabled',
  Default = 'default',
}

export const useCheckActionMutation = (eventId: string) =>
  useMutation<Check, APIError<CheckActionErrorCode>, CheckInPayload>({
    mutationKey: ['check-action', eventId],
    mutationFn: (payload: CheckInPayload) => createCheckActionFn(eventId, payload),
  });
